import { useSyncExternalStore } from "react";

// Chrome's beforeinstallprompt event isn't in the standard DOM lib types.
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

// Already running as an installed app? Then there's nothing to install.
function getIsStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // iOS Safari uses the non-standard navigator.standalone; everyone else uses
  // the display-mode media query.
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone;
  return window.matchMedia("(display-mode: standalone)").matches || iosStandalone === true;
}

// iOS Safari never fires beforeinstallprompt, so install there is a manual
// "Share -> Add to Home Screen" - we detect it to show a hint instead of a button.
function getIsIOS(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const isIOSDevice =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as a Mac but is touch-capable.
    (ua.includes("Macintosh") && "ontouchend" in document);
  // Only Safari can add to the home screen on iOS; in-app webviews can't.
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOSDevice && isSafari;
}

// --- Module-level store -----------------------------------------------------
// beforeinstallprompt fires ONCE, and often before React mounts (or before a
// lazily-mounted consumer like the mobile dropdown exists). So we capture it at
// module load into a single shared store and let every hook instance subscribe,
// rather than each component racing to attach its own listener.
let deferredEvent: BeforeInstallPromptEvent | null = null;
let installed = false;
// useSyncExternalStore needs a stable, comparable snapshot; bumping a version
// counter on every change gives it a new value to detect and re-render on.
let version = 0;
const subscribers = new Set<() => void>();

function bump() {
  version++;
  for (const s of subscribers) s();
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Stop Chrome's own mini-infobar; we drive the prompt from our button.
    e.preventDefault();
    deferredEvent = e as BeforeInstallPromptEvent;
    bump();
  });
  window.addEventListener("appinstalled", () => {
    deferredEvent = null;
    installed = true;
    bump();
  });
}

function subscribe(cb: () => void) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}
function getSnapshot() {
  return version;
}

export interface InstallPromptState {
  // Chrome/Android/desktop: the native install prompt is available right now.
  canInstall: boolean;
  // iOS Safari: no programmatic prompt, show manual "Add to Home Screen" hint.
  isIOS: boolean;
  // Already installed / launched standalone - hide all install affordances.
  isStandalone: boolean;
  // Fire the native prompt. Resolves to the user's choice (or "unavailable").
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
}

export function useInstallPrompt(): InstallPromptState {
  // Subscribe to the shared store; re-renders whenever the event lands/clears.
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const isStandalone = installed || getIsStandalone();

  async function promptInstall() {
    if (!deferredEvent) return "unavailable" as const;
    await deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    // The event can only be used once; drop it so the button hides afterwards.
    deferredEvent = null;
    bump();
    return outcome;
  }

  return {
    canInstall: !!deferredEvent && !isStandalone,
    isIOS: getIsIOS() && !isStandalone,
    isStandalone,
    promptInstall,
  };
}
